import { useEffect, useMemo, useState } from "react";

import { LOYALTY_PACKAGE_STATUS, LOYALTY_PROGRAM_FILTER_PAGE_SIZE } from "features/LoyaltyPackage/core/constants";
import {
  useLoyaltyPeriods,
  useLoyaltyPrograms,
  useSwitchLoyaltyPeriodStatus,
} from "features/LoyaltyPackage/data/hooks";
import useDebounce from "hooks/useDebounce";

export default function usePackageList() {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [pagination, setPagination] = useState({ limit: 10, offset: 0 });
  const [loyaltyPackageIds, setLoyaltyPackageIds] = useState([]);
  const [toolbarTimeRange, setToolbarTimeRange] = useState(null);
  const [columnTimeRange, setColumnTimeRange] = useState(null);
  const [programFilterOpen, setProgramFilterOpen] = useState(false);
  const [programFilterOffset, setProgramFilterOffset] = useState(0);
  const [accumulatedPrograms, setAccumulatedPrograms] = useState([]);

  const filters = useMemo(() => {
    const result = {};
    if (debouncedSearch) result.title = debouncedSearch;
    if (loyaltyPackageIds.length) result.loyaltyPackageIds = loyaltyPackageIds;
    if (toolbarTimeRange?.from) result.timeRange = toolbarTimeRange;
    if (columnTimeRange?.from) result.periodTimeRange = columnTimeRange;
    return Object.keys(result).length ? result : undefined;
  }, [debouncedSearch, loyaltyPackageIds, toolbarTimeRange, columnTimeRange]);

  const { data, loading } = useLoyaltyPeriods(filters, pagination);
  const periods = useMemo(() => data?.loyaltyPackagePeriods ?? [], [data?.loyaltyPackagePeriods]);

  const { data: programsPage, loading: programsLoading } = useLoyaltyPrograms(
    undefined,
    { offset: programFilterOffset, limit: LOYALTY_PROGRAM_FILTER_PAGE_SIZE },
    { skip: !programFilterOpen }
  );
  const { switchStatus, loading: switchLoading } = useSwitchLoyaltyPeriodStatus();

  useEffect(() => {
    if (!programFilterOpen || !programsPage?.loyaltyPackages) return;

    const page = programsPage.loyaltyPackages;
    if (programFilterOffset === 0) {
      setAccumulatedPrograms((prev) => {
        if (prev.length === page.length && page.every((item, index) => prev[index]?.id === item.id)) {
          return prev;
        }
        return page;
      });
      return;
    }

    setAccumulatedPrograms((prev) => {
      const seen = new Set(prev.map((p) => p.id));
      const appended = page.filter((p) => !seen.has(p.id));
      if (!appended.length) return prev;
      return [...prev, ...appended];
    });
  }, [programsPage, programFilterOpen, programFilterOffset]);

  const programFilterTotal = programsPage?.paginationData?.total ?? 0;
  const hasMorePrograms = accumulatedPrograms.length < programFilterTotal;
  const programsLoadingInitial = programsLoading && programFilterOffset === 0 && accumulatedPrograms.length === 0;
  const programsLoadingMore = programsLoading && programFilterOffset > 0;

  const paginationData = data?.paginationData ?? { total: 0, offset: 0, limit: 10 };

  const programOptions = useMemo(
    () =>
      accumulatedPrograms.map((program) => ({
        label: program.title,
        value: program.id,
      })),
    [accumulatedPrograms]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleTableChange = (antPagination) => {
    const { current, pageSize } = antPagination;
    setPagination({ limit: pageSize, offset: (current - 1) * pageSize });
  };

  const handleSwitchStatus = async (record, checked) => {
    await switchStatus(record.id, checked);
  };

  const handleToolbarDateFilter = (range) => {
    setToolbarTimeRange(range?.from ? range : null);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleColumnDateFilter = (range) => {
    setColumnTimeRange(range?.from ? range : null);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleProgramFilter = (ids) => {
    setLoyaltyPackageIds(ids || []);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleProgramFilterOpenChange = (visible) => {
    setProgramFilterOpen(visible);
    if (visible) {
      setProgramFilterOffset(0);
      setAccumulatedPrograms([]);
    }
  };

  const handleLoadMorePrograms = () => {
    if (!hasMorePrograms || programsLoading) return;
    setProgramFilterOffset(accumulatedPrograms.length);
  };

  const isEditable = (record) => record.status === LOYALTY_PACKAGE_STATUS.UPCOMING;

  return {
    searchTerm,
    handleSearch,
    periods,
    paginationData,
    loading,
    switchLoading,
    handleTableChange,
    handleSwitchStatus,
    isEditable,
    pagination,
    programOptions,
    programsLoading: programsLoadingInitial,
    programsLoadingMore,
    hasMorePrograms,
    loyaltyPackageIds,
    columnTimeRange,
    handleToolbarDateFilter,
    handleColumnDateFilter,
    handleProgramFilter,
    handleProgramFilterOpenChange,
    handleLoadMorePrograms,
  };
}
